import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, Mail, UserRound, HeartHandshake } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { PageHeader } from '../components/SectionHeader';
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = [
  { id: 'general', label: 'Consulta general' },
  { id: 'colabora', label: 'Colabora con Stage Crew' },
  { id: 'company', label: 'Verificación de empresa' },
  { id: 'profile', label: 'Ayuda con mi perfil' },
  { id: 'legal', label: 'Legal / protección de datos' },
  { id: 'report', label: 'Reportar contenido o usuario' },
  { id: 'partnership', label: 'Publicidad / prensa' },
];

export default function ContactoPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTopic = TOPICS.some((tp) => tp.id === searchParams.get('topic')) ? searchParams.get('topic') : 'general';
  const [form, setForm] = useState({
    name: user?.nick || user?.name || '',
    email: user?.email || '',
    topic: initialTopic,
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Completa nombre, email y mensaje');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success('Mensaje enviado a administración');
      setForm({ name: form.name, email: form.email, topic: 'general', message: '' });
    } catch {
      toast.error('No se pudo enviar. Inténtalo de nuevo.');
    } finally {
      setSending(false);
    }
  };

  const commonInput = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm';

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader
          title={initialTopic === 'colabora' ? 'Colabora con Stage Crew' : 'Contactar con administración'}
          subtitle={initialTopic === 'colabora'
            ? 'Cuéntanos qué necesitas o qué puedes aportar: contenido, manuales, formación, patrocinio o ayuda técnica. El mensaje llega directamente al administrador.'
            : 'Escríbenos para verificaciones, incidencias, colaboraciones o cualquier duda. Respondemos por correo en 1-2 días laborables.'}
          testId="contact-header"
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
          <div className="lg:col-span-2 sc-card p-6 md:p-8">
            <form className="space-y-5" onSubmit={submit} data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Nombre</label>
                  <input data-testid="contact-name" className={commonInput} value={form.name} onChange={(e) => update('name', e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Email</label>
                  <input data-testid="contact-email" type="email" className={commonInput} value={form.email} onChange={(e) => update('email', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Tema</label>
                <select data-testid="contact-topic" className={commonInput} value={form.topic} onChange={(e) => update('topic', e.target.value)}>
                  {TOPICS.map((tp) => <option key={tp.id} value={tp.id}>{tp.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Mensaje</label>
                <textarea data-testid="contact-message" rows={6} className={commonInput + ' resize-none'} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="Cuéntanos con detalle..." />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                <Send size={16} />
                {sending ? 'Enviando…' : 'Enviar mensaje'}
              </button>
              {sent && <p className="text-xs font-mono uppercase tracking-widest text-emerald-400" data-testid="contact-sent">Mensaje enviado</p>}
            </form>
          </div>

          <aside className="sc-card p-6 md:p-8 h-fit" data-testid="contact-admin-card">
            <div className="sc-mono-eyebrow text-amber-500 mb-4">Administración</div>
            <div className="flex items-center gap-2 text-white text-sm font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UserRound size={14} className="text-amber-500" />
              <span data-testid="contact-admin-name">Lucas Tornero</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-3">
              <Mail size={14} className="text-slate-500" />
              <a className="hover:text-amber-500 break-all" data-testid="contact-admin-email" href="mailto:lucasser.lighting@gmail.com">lucasser.lighting@gmail.com</a>
            </div>
            <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-6">Catalunya · España</div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">
              Para reclamaciones formales o legales, incluye tu identificación y el enlace del contenido implicado. No compartas datos sensibles de terceros.
            </p>
            <div className="pt-5 border-t border-[#2A2F3D]">
              <div className="flex items-center gap-2 text-amber-500 mb-2"><HeartHandshake size={14} /><span className="sc-mono-eyebrow text-amber-500">Colabora</span></div>
              <p className="text-xs text-slate-400 leading-relaxed">¿Tienes manuales, cursos, contactos o quieres patrocinar? Elige el tema “Colabora con Stage Crew” y cuéntanos tu necesidad.</p>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </div>
  );
}
